import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";

type Ranking = {
  id: string;
  metric: string;
  tenant_value: number | null;
  benchmark_p50: number;
  benchmark_p75: number;
  percentile: number | null;
  status: "demo" | "requires_quality_gate";
  source: string;
  agent: string;
};

function fmt(value: number | null): string {
  if (value === null) return "Quality Gate";
  if (value > 10_000) return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(value);
  return String(value);
}

export default function BenchmarksPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const rankings = useQuery({
    queryKey: ["benchmark-rankings", effectiveTenantId],
    queryFn: () => api.get<Ranking[]>("/benchmarks/rankings"),
  });
  const research = useMutation({
    mutationFn: () => api.post<{ run_id: string }>("/research/runs", {
      query: "KPI Enterprise Mining benchmark refresh for current tenant",
    }),
  });

  return (
    <section className="flow-page benchmark-page">
      <header className="flow-hero">
        <div>
          <p className="hero-eyebrow">Benchmarks</p>
          <h1>Wo stehen wir im Markt, und welche Kennzahl braucht Evidenz?</h1>
          <p className="hero-sub">
            Benchmark-Agenten nutzen nur öffentliche oder lizenzierte Quellen. Tenant-Werte bleiben gesperrt, bis das Quality Gate bestanden ist.
          </p>
        </div>
        <div className="flow-score">
          <span>Agent</span>
          <strong>{rankings.data?.[0]?.agent ?? "Benchmark"}</strong>
          <small>Crawl4AI · Nimble · Lizenzindex</small>
        </div>
      </header>

      {rankings.isLoading && <p className="muted">Lade Benchmark-Rankings...</p>}
      {rankings.error && <p className="error">Benchmarks nicht verfügbar.</p>}

      <div className="benchmark-grid">
        {(rankings.data ?? []).map((item) => (
          <article key={item.id} className={`benchmark-card ${item.status}`}>
            <div className="card-kicker">{item.status === "demo" ? "Demo-Ranking" : "Gate erforderlich"}</div>
            <h2>{item.metric}</h2>
            <div className="benchmark-values">
              <div><span>Tenant</span><strong>{fmt(item.tenant_value)}</strong></div>
              <div><span>P50</span><strong>{fmt(item.benchmark_p50)}</strong></div>
              <div><span>P75</span><strong>{fmt(item.benchmark_p75)}</strong></div>
            </div>
            <p className="muted small">
              {item.percentile === null ? "Kein Tenant-Percentile ohne validierte Quelle." : `Percentile ${item.percentile}`}
              {" · "}{item.source}
            </p>
          </article>
        ))}
      </div>

      <section className="flow-panel">
        <h2>Research-Agent starten</h2>
        <p className="muted">
          Startet einen auditierbaren Research-Run mit Crawl4AI/Nimble-Policy-Gate. Externe Modelle bleiben anonymisiert und genehmigungspflichtig.
        </p>
        <div className="flow-actions">
          <button className="btn primary" onClick={() => research.mutate()} disabled={research.isPending}>
            {research.isPending ? "Wird gestartet..." : "Benchmark-Research starten"}
          </button>
          <Link to="/app/process-landscape" className="btn ghost">In Prozess 1.6.1 öffnen</Link>
        </div>
        {research.data && <p className="success">Research Run gestartet: <code>{research.data.run_id}</code></p>}
      </section>
    </section>
  );
}
